import api from '@/lib/api';
import type { PropertyReviewStatus } from '@pms/shared';

// ─── Types ────────────────────────────────────────────────────────────────────

/** Per-property approval state surfaced to the operator (mirrors backend OnboardingReviewProperty). */
export interface OnboardingReviewProperty {
  id: string;
  name: string;
  reviewStatus: PropertyReviewStatus;
  reviewFeedback: string | null;
  submittedAt: string | null;
  reviewedAt: string | null;
}

export interface OnboardingStatus {
  step: number;
  completed: boolean;
  checklist: Record<string, boolean>;
  nextAction?: string;
  properties?: OnboardingReviewProperty[];
}

export interface SubmitForReviewResult {
  submitted: number;
  properties: OnboardingReviewProperty[];
}

export interface OnboardingPropertyData {
  name: string;
  address: string;
  city: string;
  country: string;
  description?: string | undefined;
  propertyType: string;
  timezone?: string | undefined;
  currency?: string | undefined;
}

export interface OnboardingUnitItem {
  name: string;
  type: string;
  capacity: number;
  basePrice: number;
}

export interface OnboardingUnitsData {
  propertyId: string;
  units: OnboardingUnitItem[];
}

export interface OnboardingPricingItem {
  unitId: string;
  basePrice: number;
  weekendPrice?: number | undefined;
  minimumStay?: number | undefined;
}

export interface OnboardingPricingData {
  units: OnboardingPricingItem[];
}

export interface OnboardingPoliciesData {
  propertyId: string;
  checkInTime: string;
  checkOutTime: string;
  cancellationPolicy: string;
  houseRules?: string | undefined;
  petPolicy?: string | undefined;
  smokingPolicy?: string | undefined;
}

// ─── API functions ────────────────────────────────────────────────────────────

export async function fetchOnboardingStatus(): Promise<OnboardingStatus> {
  const { data } = await api.get<OnboardingStatus>('/onboarding/status');
  return data;
}

export async function submitForReview(): Promise<SubmitForReviewResult> {
  const { data } = await api.post<SubmitForReviewResult>('/onboarding/submit-for-review');
  return data;
}

export async function createOnboardingProperty(payload: OnboardingPropertyData) {
  const { data } = await api.post('/onboarding/property', payload);
  return data;
}

export async function createOnboardingUnits(payload: OnboardingUnitsData) {
  const { data } = await api.post('/onboarding/units', payload);
  return data;
}

export async function configureOnboardingPricing(payload: OnboardingPricingData) {
  const { data } = await api.post('/onboarding/pricing', payload);
  return data;
}

export async function configureOnboardingPolicies(payload: OnboardingPoliciesData) {
  const { data } = await api.post('/onboarding/policies', payload);
  return data;
}

export async function completeOnboarding() {
  const { data } = await api.post('/onboarding/complete');
  return data;
}

export async function skipOnboarding() {
  const { data } = await api.post('/onboarding/skip');
  return data;
}
